/**
 * @file useTableContextMenu.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-17
 * @modified 2026-07-17
 * @description State + event wiring for a table right-click context menu. Pairs
 *              with the TableContextMenu component, which does the rendering.
 *
 *              Right-click on a row opens the menu for that row; right-click on
 *              the table's dead space opens it with `row: null` (generic actions
 *              only). Holding the platform modifier (Cmd on Mac, Ctrl elsewhere)
 *              suppresses our menu and lets the browser's own through — which is
 *              also what the global radial ContextMenu listens for, so the two
 *              never fight over the same gesture.
 *
 *              Right-click never mutates selection — checking rows is the user's
 *              job, so a right-click can't tick a checkbox out from under them
 *              (which would also trip the pages' "N selected" bulk action bars).
 *              Bulk actions therefore surface only when the user has already
 *              checked >1 row AND right-clicks one of those checked rows;
 *              right-clicking any other row acts on that row alone.
 *
 *              Usage:
 *                const ctx = useTableContextMenu({
 *                  rows, selectedIds, getRowId: r => r.id
 *                });
 *                <tr onContextMenu={ctx.onRowContextMenu(item)}>
 *                <table onContextMenu={ctx.onTableContextMenu}>
 *                <TableContextMenu ctx={ctx} rowActions={...} bulkActions={...} />
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const CLOSED = { open: false, x: 0, y: 0, row: null };

function isModifierHeld(e) {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/i.test(navigator.platform);
  return isMac ? e.metaKey : e.ctrlKey;
}

export default function useTableContextMenu({
  rows = [],
  selectedIds = null,
  getRowId = (r) => r?.id
} = {}) {
  const [menu, setMenu] = useState(CLOSED);

  const close = useCallback(() => setMenu(c => (c.open ? { ...c, open: false } : c)), []);

  const openAt = useCallback((e, row) => {
    if (isModifierHeld(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setMenu({ open: true, x: e.clientX, y: e.clientY, row: row || null });
  }, []);

  const onRowContextMenu = useCallback((row) => (e) => openAt(e, row), [openAt]);
  const onTableContextMenu = useCallback((e) => openAt(e, null), [openAt]);

  // Close on Escape / scroll / resize. The overlay handles click-away itself.
  useEffect(() => {
    if (!menu.open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    document.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [menu.open, close]);

  const targets = useMemo(() => {
    const sel = selectedIds || new Set();
    const rowId = menu.row ? getRowId(menu.row) : null;
    // Bulk only when the right-clicked row is part of a multi-row selection.
    const bulk = sel.size > 1 && (rowId === null || sel.has(rowId));
    const ids = bulk ? new Set(sel) : (rowId !== null ? new Set([rowId]) : new Set());
    return {
      bulk,
      ids,
      count: ids.size,
      rows: rows.filter(r => ids.has(getRowId(r)))
    };
  }, [menu.row, selectedIds, rows, getRowId]);

  return {
    ...menu,
    close,
    reset: useCallback(() => setMenu(CLOSED), []),
    onRowContextMenu,
    onTableContextMenu,
    ...targets
  };
}
